import React, { useEffect, useState } from 'react';
import {
  CatalogFileCollection,
  CatalogFileCollectionObjectTag,
  PriceData,
} from '../../../common/libraries/fileFormats/catalog/catalog';
import {
  NavigationReducer,
  NavigationReducerDataFilterTags,
} from '../../../Redux/Reducers/NavigationReducer';
import { useAppDispatch, useAppSelector } from '../../../Redux/Store/hooks';
import { Collection } from '../../Data/Collection';
import { CollectionFilter } from '../../Data/CollectionFilter';
import { CollectionToShow, ICollectionToShow } from '../../Data/CollectionToShow';
import { POSCatalogData } from '../../../common/libraries/modelViewer/Data/POSCatalogData';
import { CatalogObjectDisplay } from '../../Util/CatalogObjectDisplay';
import { CollectionView } from '../Views/CollectionView';

type CollectionControllerParameters = {
  catalog : POSCatalogData,
  catalogCollections: CatalogFileCollection[]
};

export function CollectionController(
  {
    catalog,
    catalogCollections,
  } : CollectionControllerParameters,
) {

  // Grab the dispatch hook from Redux
  const dispatch = useAppDispatch();

  // Grab the dispatchable actions we need
  const applicationActions = NavigationReducer.actions;

  // From the store, get the filters
  const itemsFilterSelection = useAppSelector((state) => {
    return state.NavigationReducer.filter;
  });

  // From the store, get the collectionName
  const collectionName = useAppSelector((state) => {
    return state.NavigationReducer.collection;
  });

  // From the store, get the environment settings
  const environmentSettings = useAppSelector((state) => {
    return state.EnvironmentSettingsReducer;
  });

  //-----------------------------------------
  // Data to send to the childs

  // Set of filters that we want to show
  const [
    filtersToDisplay,
    setFiltersToDisplay] = useState<CollectionFilter[]>([]);

  // Collections / items to view
  const [
    collectionsToDisplay,
    setCollectionsToDisplay] = useState<ICollectionToShow[]>([]);

  const collectionConstructor = (
    collection: CatalogFileCollection,
    id : number,
  ) : CollectionToShow => {

    const result = new CollectionToShow();

    result.id = id;
    result.name = collection.name;
    result.objectNumber = collection.objects.length;
    result.objects = [];

    return result;
  };

  const filtersConstructor = (
    catalogObjectTags: CatalogFileCollectionObjectTag[],
    filters_list: CollectionFilter[],
  ) : CollectionFilter[] => {

    catalogObjectTags.forEach((catalogObjectTag) => {
      let newFilter = filters_list.find(
        (f) => f.name === catalogObjectTag.type,
      );
      if (!newFilter) {
        newFilter = {
          name: catalogObjectTag.type,
          options: [catalogObjectTag.value],
        };
      } else {
        // eslint-disable-next-line no-param-reassign
        filters_list = filters_list.filter(
          (f) => f.name !== catalogObjectTag.type,
        );
        if (!newFilter.options.find((opt) => opt === catalogObjectTag.value)) {
          newFilter.options.push(catalogObjectTag.value);
        }
      }

      filters_list.push(newFilter);
    });

    return filters_list;
  };

  const LoadCollections = () => {
    let validateObjectCriteria = false;

    if (
      itemsFilterSelection
      && itemsFilterSelection.tags
      && itemsFilterSelection.tags.length > 0
    ) {
      validateObjectCriteria = true;
    }

    const arrCollectionsToShow : Array<ICollectionToShow> = [];
    let arrFiltersToShow : Array<CollectionFilter> = [];

    catalogCollections.forEach((collection) => {
      // Check if we should include this collection to the collections to send to the view.
      // We shall include it, unless the collection name specified a concrete collection name
      // and it's not this one

      let includeCollection = false;
      if (collectionName === Collection.AllCollections) {
        includeCollection = true;
      } else if (collectionName === collection.name) {
        includeCollection = true;
      }

      if (includeCollection) {
        // Build an object to represent a collection in the controller
        const controllerCollection = collectionConstructor(
          collection,
          collection.id,
        );

        // Go through the catalog objects, check if they should be added.
        // Even if they are filtered out, grab their value to build the filters
        // otherwise, it would be impossible to select them

        const arrCatalogObjects = collection.objects;

        for (
          let curCatalogObject = 0;
          curCatalogObject < arrCatalogObjects.length;
          curCatalogObject++
        ) {
          const catalogObject = arrCatalogObjects[curCatalogObject];

          let insertObject = true;

          if (validateObjectCriteria) {
            const filtersToCheck = itemsFilterSelection!.tags;
            // Grab the object properties
            const catalogObjectTags = catalogObject.tags;

            // Check if the object passes the filters criteria
            for (
              let curFilterToCheck = 0;
              curFilterToCheck < filtersToCheck.length;
              curFilterToCheck++
            ) {
              const filterToCheck = filtersToCheck[curFilterToCheck];

              const matchedResult = catalogObjectTags.find(
                (catalogObjectTag) => {
                  return (
                    catalogObjectTag.type === filterToCheck.type
                    && catalogObjectTag.value === filterToCheck.value
                  );
                },
              );

              if (!matchedResult) {
                insertObject = false;
                break;
              }
            }
          }

          // Grab the filters properties of the object
          arrFiltersToShow = filtersConstructor(
            catalogObject.tags,
            arrFiltersToShow,
          );

          if (insertObject) {

            const posCatalogModelData = catalog.getModelByCollectionIDAndModelID(
              collection.id,
              catalogObject.id,
            );

            if (posCatalogModelData) {
              const newDisplayObject = CatalogObjectDisplay.CreateCatalogObjectDisplayObject(
                controllerCollection,
                posCatalogModelData,
                curCatalogObject,
                environmentSettings.catalogInfoPath,
                catalog.catalogFileInfo?.priceData || new Array<PriceData>(),
              );

              controllerCollection.objects.push(newDisplayObject);
            }

          }
          controllerCollection.objects.sort((a, b) => {
            return a.displayPriority.localeCompare(b.displayPriority);
          });
        }

        // Add the collection object to the array
        arrCollectionsToShow.push(controllerCollection);
      }
    });

    // Build the selection of filters that should be selected in the view

    setCollectionsToDisplay(arrCollectionsToShow);
    setFiltersToDisplay(arrFiltersToShow);
  };

  useEffect(() => {
    LoadCollections();
  }, [itemsFilterSelection, collectionName]);

  // Callback to handle the changes in the filter in the child component
  const updateFilterSelection = (newFilterSelection : NavigationReducerDataFilterTags[]) => {
    dispatch(
      applicationActions.updateFilterTags({ filters: newFilterSelection }),
    );
  };

  if (collectionsToDisplay) {
    return (
      <CollectionView
        filters={filtersToDisplay}
        filterSelection={
        itemsFilterSelection ? itemsFilterSelection.tags : undefined
      }
        collections={collectionsToDisplay}
        setFilterSelection={updateFilterSelection}
      />
    );
  }

  return (<div className="empty" />);
}
